/**
 * Status-effect aura visuals — single knob file (config-driven, 2026-06-12).
 *
 * Server-applied statuses carry plain ids ('burn', 'poison', 'slow',
 * 'frozen', …, from HitEffects → applyStatusEffect → StatusEffectService)
 * that were NEVER registered VFX effects — so the status aura resolved to
 * nothing on EVERY client (the caster's local prediction included; the
 * one-shot splash at hit position was the only feedback). This map renders
 * each status as the registered `defensive_aura` shape tinted per status —
 * one base effect, data-driven hues, same pixels on every client.
 *
 * VFXSystem.resolveStatusEffectId consults this AFTER the raw-id registry
 * check, so a future bespoke 'burn' effect def automatically wins over the
 * tinted fallback.
 */
export interface StatusAuraStyle {
  /** Registered VFX effect id used as the aura base shape. */
  effectId: string;
  /** Tint applied to the base shape. */
  color: number;
}

export const STATUS_AURA_CONFIG: Record<string, StatusAuraStyle> = {
  // u0 P2 "buff flat-tint" resolved (2026-07-17): each status now has a
  // bespoke silhouette (StatusVFX.ts) instead of one tinted defensive_aura —
  // burn=embers, poison=drips, slow=drag ring, frozen=shards, stun=orbiting
  // stars, regen=rising motes. The color stays as metadata for FCT/labels;
  // multi-tone defs keep their own palettes (VFXEffectPlayer palette lock).
  burn:   { effectId: 'status_burn',   color: 0xFF4500 },
  poison: { effectId: 'status_poison', color: 0x44CC44 },
  slow:   { effectId: 'status_slow',   color: 0x66CCFF },
  frozen: { effectId: 'status_frozen', color: 0x99DDFF },
  stun:   { effectId: 'status_stun',   color: 0xFFDD44 },
  regen:  { effectId: 'status_regen',  color: 0x44FF88 },
  // Warlock affliction family (F6 expansion 2026-07-17) — these ids ride the
  // live sendServerStatus lane onto VICTIMS and previously rendered NOTHING
  // (raw unregistered ids). immolate is a true burn — honest alias, not a
  // second fire look.
  agony:                { effectId: 'status_agony',                color: 0xAA1122 },
  corruption:           { effectId: 'status_corruption',           color: 0x551188 },
  immolate:             { effectId: 'status_burn',                 color: 0xFF4500 },
  unstable_affliction:  { effectId: 'status_unstable_affliction',  color: 0xDD22AA },
  curse_of_elements:    { effectId: 'status_curse_of_elements',    color: 0x6644AA },
  // Canonical consumable effect ids. These are server-broadcast status ids,
  // so mapping them here makes their persistent world aura visible to the
  // consumer and every observer.
  heal_over_time_100:   { effectId: 'status_regen',                  color: 0x44FF88 },
  buff_speed_60:        { effectId: 'status_haste',                  color: 0x00FFAA },
  buff_fortitude_100:   { effectId: 'status_consumable_fortitude',  color: 0xEAB308 },
  buff_titans_allstats: { effectId: 'status_consumable_titans',     color: 0xA855F7 },
  buff_attack_10:       { effectId: 'status_consumable_attack',     color: 0xDC2626 },
  buff_defense_10:      { effectId: 'status_consumable_defense',    color: 0x2563EB },
  buff_experience_2x:   { effectId: 'status_consumable_experience', color: 0xC084FC },
};
